import { Box, Typography } from "@mui/material";
import { Dot } from "lucide-react";
import { RetrievalResult } from "protogen/search_service_pb";
import DateDisplay from "../common/DateDisplay";
import { iconForResult, resultTypeLabel, urlForResult } from "./utils";
import LinkRouter from "../navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";

type Props = {
  result: RetrievalResult;
  onClose: () => void;
};

export default ({ result, onClose }: Props) => {
  const entryDt = new Date(Number(result.indexedSec) * 1000);
  const isMobile = useIsMobile();
  const metadata = new Map(
    result.metadata.map((item) => [item.key, item.value]),
  );
  const familyName = metadata.get("familyName");
  const showFamilyName = familyName && familyName !== result.sourceName;

  return (
    <LinkRouter to={urlForResult(result)} onClick={onClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          cursor: "pointer",
          borderRadius: "16px",
          border: "1px solid #D4D4D4",
          alignItems: "flex-start",
          padding: isMobile ? "16px 24px" : "20px 24px 22px",
          gap: "12px",
        }}
      >
        {!isMobile && (
          <Box sx={{ color: "#8E9598" }}>{iconForResult(result)}</Box>
        )}
        <Box
          sx={{
            flex: "1 1 auto",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            overflow: "hidden",
          }}
        >
          {isMobile && (
            <Typography
              variant="bodySmall"
              color="#198282"
              sx={{ fontWeight: 700 }}
            >
              {resultTypeLabel(result)}
            </Typography>
          )}
          <Typography
            variant="bodyHeavy"
            sx={{
              wordBreak: "break-word",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
            }}
          >
            {result.text}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {result.sourceName && (
              <Typography variant="bodySmall" color="#6B6E7B">
                {result.sourceName}
              </Typography>
            )}
            {showFamilyName && (
              <>
                {result.sourceName && <Dot color="#6B6E7B" />}
                <Typography variant="bodySmall" color="#6B6E7B">
                  {familyName}
                </Typography>
              </>
            )}
            {(result.sourceName || showFamilyName) && <Dot color="#6B6E7B" />}
            <DateDisplay
              date={entryDt}
              sx={{ fontSize: "14px", color: "#6B6E7B" }}
            />
          </Box>
          <Typography
            variant="bodySmall"
            color="#6B6E7B"
            sx={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 3,
              overflow: "hidden",
              whiteSpace: "pre-wrap",
            }}
          >
            {result.subText}
          </Typography>
        </Box>
      </Box>
    </LinkRouter>
  );
};
